import React, { useEffect, useState } from 'react';
import { HiPlus, HiPencil, HiTrash, HiX } from 'react-icons/hi';
import toast from 'react-hot-toast';
import ConfirmDialog from '../../components/ConfirmDialog';
import { planTypesApi } from '../../services/api';

const EMPTY_FORM = {
  name: '',
  target_sessions: 90,
  mon: true,
  tue: true,
  wed: true,
  thu: true,
  fri: true,
  sat: false,
  sun: false,
  is_active: true,
};

const PlanTypes = () => {
  const [planTypes, setPlanTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, onConfirm: null });

  const fetchPlanTypes = async () => {
    setLoading(true);
    try {
      const response = await planTypesApi.getAll();
      setPlanTypes(response?.data?.data || []);
    } catch {
      toast.error('Failed to fetch plan types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanTypes();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setFormData({
      name: item.name || '',
      target_sessions: Number(item.target_sessions) || 90,
      mon: !!item.mon,
      tue: !!item.tue,
      wed: !!item.wed,
      thu: !!item.thu,
      fri: !!item.fri,
      sat: !!item.sat,
      sun: !!item.sun,
      is_active: !!item.is_active,
    });
    setShowForm(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const enabledDays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].some((d) => formData[d]);
    if (!enabledDays) {
      toast.error('Select at least one weekday');
      return;
    }
    try {
      if (editing) {
        await planTypesApi.update(editing.id, formData);
        toast.success('Plan type updated');
      } else {
        await planTypesApi.create(formData);
        toast.success('Plan type created');
      }
      setShowForm(false);
      fetchPlanTypes();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save plan type');
    }
  };

  const onDelete = (id) => {
    setConfirmDialog({
      isOpen: true,
      onConfirm: async () => {
        try {
          await planTypesApi.delete(id);
          toast.success('Plan type deleted');
          fetchPlanTypes();
        } catch (error) {
          toast.error(error?.response?.data?.message || 'Failed to delete plan type');
        }
      },
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Plan Types</h1>
          <p className="text-gray-500 mt-1">Configure weekday schedules for 90-session countdown</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
          <HiPlus className="w-5 h-5" />
          Add Plan Type
        </button>
      </div>

      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {planTypes.map((pt) => (
            <div key={pt.id} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{pt.name}</p>
                  <p className="text-xs text-gray-500 mt-1">Target: {pt.target_sessions} sessions</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(pt)} className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg"><HiPencil className="w-4 h-4" /></button>
                  <button onClick={() => onDelete(pt.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"><HiTrash className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((d) => (
                  <span key={d} className={`px-2 py-0.5 rounded-full text-xs ${pt[d] ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {d.toUpperCase()}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-xl w-full mx-4">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{editing ? 'Edit Plan Type' : 'Add Plan Type'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><HiX className="w-6 h-6" /></button>
            </div>
            <form onSubmit={onSubmit} className="p-6 space-y-4">
              <input className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Name" value={formData.name} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} required />
              <input className="w-full px-3 py-2 border border-gray-300 rounded-lg" type="number" min="1" value={formData.target_sessions} onChange={(e) => setFormData((p) => ({ ...p, target_sessions: Number(e.target.value) || 90 }))} />
              <div className="grid grid-cols-4 gap-2">
                {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((d) => (
                  <label key={d} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!formData[d]} onChange={(e) => setFormData((p) => ({ ...p, [d]: e.target.checked }))} />
                    {d.toUpperCase()}
                  </label>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!formData.is_active} onChange={(e) => setFormData((p) => ({ ...p, is_active: e.target.checked }))} />Active</label>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" className="px-4 py-2 border border-gray-300 rounded-lg" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, onConfirm: null })}
        onConfirm={confirmDialog.onConfirm || (() => {})}
        title="Delete Plan Type"
        message="Are you sure you want to delete this plan type?"
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default PlanTypes;
